/**
 * Project Shares API module — CRUD for per-user project access grants.
 * Only project owners can manage shares.
 */
import { fetchApi } from './client';
import { log } from '@/lib/logger';
import { MUTATION_TIMEOUT_MS, SCREEN_READ_TIMEOUT_MS } from './requestTimeout';

log.info('[api/projectShares] Module loaded');

export interface ProjectShare {
  id: string;
  projectId: string;
  userId: string;
  role: 'viewer' | 'collaborator';
  user: { id: string; name: string; email: string; avatarUrl: string | null };
  createdAt: string;
}

export const projectSharesApi = {
  list: (token: string | undefined, projectId: string) => {
    log.info('[api/projectShares] list', { projectId });
    return fetchApi<ProjectShare[]>(`/projects/${projectId}/shares`, { token, timeoutMs: SCREEN_READ_TIMEOUT_MS });
  },

  create: (token: string | undefined, projectId: string, userId: string, role: 'viewer' | 'collaborator') => {
    log.info('[api/projectShares] create', { projectId, userId, role });
    return fetchApi<ProjectShare>(`/projects/${projectId}/shares`, {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify({ userId, role }),
      // This form owns its 400 (self-share refusal): it renders the server
      // message inline, so the refusal is not a crash. Logs at warn instead of
      // minting a fake $exception. See errorLogLevel.ts.
      expectedErrorStatuses: [400],
    });
  },

  update: (token: string | undefined, projectId: string, shareId: string, role: 'viewer' | 'collaborator') => {
    log.info('[api/projectShares] update', { projectId, shareId, role });
    return fetchApi<ProjectShare>(`/projects/${projectId}/shares/${shareId}`, {
      token,
      method: 'PATCH',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify({ role }),
    });
  },

  remove: (token: string | undefined, projectId: string, shareId: string) => {
    log.info('[api/projectShares] remove', { projectId, shareId });
    return fetchApi<{ deleted: boolean }>(`/projects/${projectId}/shares/${shareId}`, {
      token,
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
    });
  },
};
